import {
  BookOpenText,
  MessagesSquare,
  ScrollText,
  Fingerprint,
  Ban,
  EyeOff,
  ShieldAlert,
  Skull,
  Swords,
  Wand2,
  Crosshair,
  HeartHandshake,
  Lock,
  ShieldCheck,
  Gavel,
  Sparkles,
} from "lucide-react";
import { ASSETS } from "@/assets";
import { Reveal } from "@/components/Reveal";
import { RULES_PAGE } from "@/constants/testIds";

const SECTIONS = [
  { id: "filozofie", icon: BookOpenText, title: "Základní filozofie serveru" },
  { id: "roleplay", icon: MessagesSquare, title: "Roleplay je na prvním místě" },
  { id: "lore", icon: ScrollText, title: "Lore" },
  { id: "postava", icon: Fingerprint, title: "Tvorba postavy" },
  { id: "powergaming", icon: Ban, title: "Powergaming" },
  { id: "metagaming", icon: EyeOff, title: "Metagaming" },
  { id: "godmodding", icon: ShieldAlert, title: "Godmodding" },
  { id: "smrt", icon: Skull, title: "Smrt postavy" },
  { id: "boj", icon: Swords, title: "Boj" },
  { id: "dm", icon: Wand2, title: "Dungeon Master" },
  { id: "pvp", icon: Crosshair, title: "PvP" },
  { id: "respekt", icon: HeartHandshake, title: "Respekt" },
  { id: "erp", icon: Lock, title: "ERP" },
  { id: "admin", icon: ShieldCheck, title: "Admin tým" },
  { id: "tresty", icon: Gavel, title: "Tresty" },
];

const RuleList = ({ items, ordered }) => {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`space-y-1.5 pl-5 ${ordered ? "list-decimal" : "list-disc"} marker:text-[#F0801F]`}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </Tag>
  );
};

const CompareExample = ({ wrong, right }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
    <div className="rounded-md border border-red-500/20 bg-red-500/5 px-4 py-3">
      <p className="font-display text-xs font-600 uppercase tracking-[0.14em] text-red-400">Špatně</p>
      <p className="mt-1.5 text-sm text-white/70">{wrong}</p>
    </div>
    <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
      <p className="font-display text-xs font-600 uppercase tracking-[0.14em] text-emerald-400">Správně</p>
      <p className="mt-1.5 text-sm text-white/70">{right}</p>
    </div>
  </div>
);

const RuleSection = ({ num, id, icon: Icon, title, children }) => (
  <div id={id} className="scroll-mt-28 px-6 py-4" data-testid={RULES_PAGE.ruleSection}>
    <Reveal className="mx-auto max-w-[900px] rounded-xl border border-white/10 bg-[#17171b] p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#F0801F]/30 text-[#F0801F]">
          <Icon className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-xs font-600 uppercase tracking-[0.25em] text-[#F0801F]">
            Pravidlo {num}
          </p>
          <h2 className="mt-1.5 font-display text-xl font-600 uppercase tracking-tight text-white sm:text-2xl">
            {title}
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-white/60">{children}</div>
        </div>
      </div>
    </Reveal>
  </div>
);

export const RulesPage = () => {
  return (
    <section data-testid={RULES_PAGE.section}>
      {/* Hero */}
      <div className="relative overflow-hidden px-6 pb-16 pt-40" data-testid={RULES_PAGE.heroSection}>
        <img
          src={ASSETS.heroCastle}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-[65%_center] brightness-[0.85]"
        />
        <div className="absolute inset-0 bg-[#141417]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#141417]/70 to-[#141417]" />
        <div className="relative mx-auto max-w-[900px] text-center">
          <Reveal>
            <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">Pravidla</p>
            <h1 className="mt-4 font-display text-4xl font-600 uppercase tracking-tight text-white sm:text-5xl">
              Pravidla serveru
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/60">
              Draconia RP je příběhový server inspirovaný Dungeons &amp; Dragons a světem
              Forgotten Realms. Hlavním cílem není vítězství nad ostatními hráči, ale
              společné vytváření kvalitních příběhů, uvěřitelných postav a nezapomenutelných
              dobrodružství. Každý hráč je spoluautorem světa - hraj tak, aby sis to užil ty
              i ostatní.
            </p>
          </Reveal>
        </div>
      </div>

      {/* TOC */}
      <div className="px-6 pb-10">
        <Reveal
          className="mx-auto flex max-w-[900px] flex-wrap justify-center gap-2"
          data-testid={RULES_PAGE.toc}
        >
          {SECTIONS.map((s, i) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={RULES_PAGE.tocLink}
              className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 font-display text-xs font-500 uppercase tracking-[0.08em] text-white/70 transition-colors hover:border-[#F0801F]/50 hover:text-[#F0801F]"
            >
              {i + 1}. {s.title}
            </a>
          ))}
        </Reveal>
      </div>

      <div className="pb-10">
        <RuleSection num={1} id="filozofie" icon={BookOpenText} title="Základní filozofie serveru">
          <p>
            Tento server je zaměřen na příběhové (RP) hraní inspirované světem Dungeons &amp;
            Dragons, konkrétně prostředím Forgotten Realms (Zapomenuté říše).
          </p>
          <p>
            Server využívá engine a klienta World of Warcraft pouze jako technickou platformu.
            Veškeré modely, animace, prostředí a herní mechaniky slouží pouze jako prostředek
            k odehrávání příběhů. Lore Warcraftu se na tomto serveru nepoužívá.
          </p>
          <p>
            Hlavním cílem není vítězství nad ostatními hráči, ale společné vytváření kvalitních
            příběhů, uvěřitelných postav a nezapomenutelných dobrodružství.
          </p>
          <p>
            Každý hráč je spoluautorem světa. Chovej se tak, aby sis hru užil nejen ty, ale
            i ostatní.
          </p>
        </RuleSection>

        <RuleSection num={2} id="roleplay" icon={MessagesSquare} title="Roleplay je na prvním místě">
          <p>Veškerá veřejná komunikace během RP probíhá In Character (IC).</p>
          <p>
            Komunikace mimo roli (OOC) patří pouze do určených kanálů nebo musí být jasně
            označena, například:
          </p>
          <div className="flex flex-wrap gap-2">
            <code className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70">
              (( text ))
            </code>
            <code className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70">
              [OOC]
            </code>
          </div>
          <p>Vyhýbej se moderním výrazům jako lol, brb, afk nebo omg během RP.</p>
          <p>
            OOC text v závorkách přímo v /say nebo /emote (třeba právě brb nebo afk) slouží
            výhradně k tomu, abys na něco RP-relevantního upozornil hráče ve své bezprostřední
            blízkosti - typicky že musíš na chvilku odběhnout. V žádném případě do něj nepatří
            běžné mimoherní pokecávání (co jsi měl dnes k obědu apod.) - od toho je tu general
            chat, guild chat a další OOC kanály.
          </p>
          <p>
            RP servery jsou založeny na oddělení IC a OOC komunikace a na zachování imerze.
          </p>
        </RuleSection>

        <RuleSection num={3} id="lore" icon={ScrollText} title="Lore">
          <p>
            Server vychází výhradně z prostředí Forgotten Realms (Zapomenuté říše) ze světa
            Dungeons &amp; Dragons. To znamená:
          </p>
          <RuleList
            items={[
              "respektuj historii Faerûnu a Torilu,",
              "používej bohy, národy a organizace odpovídající Forgotten Realms,",
              "nevymýšlej vlastní kontinenty ani bohy (u bohů je možné domluvit se s DM týmem na výjimce otevřením ticketu na Discordu),",
              "neměň historii známých států, měst nebo organizací.",
            ]}
          />
          <p>
            Menší vlastní příběhy, vesnice, cechy nebo organizace jsou vítány, pokud
            nenarušují hlavní lore. Výjimkou z pravidla o bozích jsou patroni warlocků - ty
            si vymyslet můžeš.
          </p>
          <p>
            Modely, lokace nebo objekty převzaté z klienta World of Warcraft představují
            pouze vizuální ztvárnění prvků ze Zapomenutých říší a nemají žádný příběhový
            význam podle Warcraftu.
          </p>
        </RuleSection>

        <RuleSection num={4} id="postava" icon={Fingerprint} title="Tvorba postavy">
          <p>Každá postava musí mít:</p>
          <RuleList items={["uvěřitelné jméno,", "uvěřitelný původ,", "motivaci,", "silné i slabé stránky."]} />
          <p>Nevytvářej postavy typu:</p>
          <RuleList
            items={[
              "vyvolený spasitel světa,",
              "potomek božstva,",
              "tajný vládce mocné organizace,",
              "nejsilnější mág všech dob,",
              "nezabitelný válečník.",
            ]}
          />
          <p>
            Takové postavy narušují RP. Stejně tak se vyžadují jména odpovídající fantasy
            prostředí Forgotten Realms.
          </p>
        </RuleSection>

        <RuleSection num={5} id="powergaming" icon={Ban} title="Powergaming">
          <p>Powergaming je zakázán.</p>
          <CompareExample wrong="Setnu ti hlavu." right="Rozmáchnu se mečem směrem k tvému krku." />
          <p>O výsledku rozhoduje:</p>
          <RuleList
            items={[
              "druhý hráč,",
              "hod kostkou - rollový souboj (na hody) je defaultním způsobem řešení sporů,",
              "Dungeon Master.",
            ]}
          />
        </RuleSection>

        <RuleSection num={6} id="metagaming" icon={EyeOff} title="Metagaming">
          <p>Je zakázáno využívat informace, které tvoje postava nemůže znát. Například:</p>
          <RuleList items={["Discord,", "OOC chat,", "stream,", "informace z jiné postavy."]} />
          <p>Postava ví pouze to, co sama zažila nebo se dozvěděla během RP.</p>
        </RuleSection>

        <RuleSection num={7} id="godmodding" icon={ShieldAlert} title="Godmodding">
          <p>Nikdo není neporazitelný. Zakázané jsou například:</p>
          <RuleList
            items={[
              "automatické zásahy,",
              "automatické zabití,",
              "ignorování zranění,",
              "neomezená magie,",
              "nezastavitelné schopnosti.",
            ]}
          />
          <p>Každá akce má následky.</p>
        </RuleSection>

        <RuleSection num={8} id="smrt" icon={Skull} title="Smrt postavy">
          <p>Smrt je významná událost. Postava může zemřít pouze:</p>
          <RuleList
            items={["po dohodě hráčů,", "rozhodnutím Dungeon Mastera,", "případně na základě posouzení vlastníka postavy."]}
          />
          <p>
            Po smrti nelze používat informace získané před smrtí, pokud to příběh výslovně
            neumožňuje.
          </p>
        </RuleSection>

        <RuleSection num={9} id="boj" icon={Swords} title="Boj">
          <p>Každý boj má být příběhový. Preferujeme:</p>
          <RuleList items={["popisy akcí,", "emote RP,", "hody kostkou,", "rozhodnutí Dungeon Mastera."]} />
          <p>Cílem není „vyhrát“, ale vytvořit zajímavou scénu.</p>
        </RuleSection>

        <RuleSection num={10} id="dm" icon={Wand2} title="Dungeon Master">
          <p>DM/GM je vypravěč příběhu. Má právo:</p>
          <RuleList
            items={[
              "řídit události,",
              "ovládat NPC,",
              "rozhodovat spory,",
              "vytvářet questy,",
              "měnit obtížnost střetnutí,",
              "udělovat odměny.",
            ]}
          />
          <p>Rozhodnutí DM/GM jsou během probíhající události konečná.</p>
        </RuleSection>

        <RuleSection num={11} id="pvp" icon={Crosshair} title="PvP">
          <p>PvP musí mít RP důvod. Nepovoluje se:</p>
          <RuleList items={["náhodné zabíjení,", "griefing,", "spawn killing,", "zabíjení bez příběhového důvodu."]} />
          <p>Každý konflikt musí dávat smysl.</p>
        </RuleSection>

        <RuleSection num={12} id="respekt" icon={HeartHandshake} title="Respekt">
          <p>Každý hráč si zaslouží respekt. Zakázáno je:</p>
          <RuleList
            items={["urážení,", "trolling,", "obtěžování,", "diskriminace,", "úmyslné narušování RP ostatních."]}
          />
          <p>Úmyslné kazení příběhu nebo narušování roleplaye ostatních hráčů nebude tolerováno.</p>
        </RuleSection>

        <RuleSection num={13} id="erp" icon={Lock} title="ERP">
          <p>Erotické RP je povoleno pouze pokud:</p>
          <RuleList
            items={[
              "s ním souhlasí všichni účastníci,",
              "probíhá mimo veřejná RP místa,",
              "je vedeno do soukromých, neveřejných kanálů.",
            ]}
          />
        </RuleSection>

        <RuleSection num={14} id="admin" icon={ShieldCheck} title="Admin tým">
          <p>Admin tým má právo:</p>
          <RuleList
            items={["řešit stížnosti,", "kontrolovat porušení pravidel,", "udělovat tresty,", "upravit pravidla podle potřeb serveru."]}
          />
        </RuleSection>

        <RuleSection num={15} id="tresty" icon={Gavel} title="Tresty">
          <p>Možné postihy:</p>
          <RuleList
            ordered
            items={["Domluva.", "Varování.", "Dočasné omezení RP.", "Kick.", "Dočasný ban.", "Trvalý ban."]}
          />
          <p>Výše trestu závisí na závažnosti porušení.</p>
        </RuleSection>
      </div>

      {/* Zlaté pravidlo */}
      <div className="px-6 pb-24" data-testid={RULES_PAGE.goldenRuleSection}>
        <Reveal className="mx-auto max-w-[900px] rounded-xl border border-[#F0801F]/30 bg-[#17171b] px-6 py-14 text-center sm:px-10">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-[#F0801F]/30 text-[#F0801F]">
            <Sparkles className="h-6 w-6" strokeWidth={1.4} />
          </span>
          <p className="mt-5 font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
            Zlaté pravidlo
          </p>
          <h2 className="mt-3 font-display text-2xl font-600 uppercase tracking-tight text-white sm:text-3xl">
            Hraj tak, aby sis hru užil nejen ty, ale i ostatní
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/60">
            Dobrý roleplay není o tom být nejsilnější postavou, ale o společném vyprávění
            příběhu, ve kterém mají prostor zazářit všichni.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
